/**
 * Registering a work into the studio book: the four-step draft, what stops
 * each step, and the request that mints the work.
 *
 * Pure. A typed draft and the pending images go in; refusals, confirm rows
 * and the request body come out. The copy is `estate.constants.ts`; the
 * dialog's stage, open flag, image intake and the submit are
 * `useRegisterStudioController.ts`; the image screening it reuses is the
 * collector's `register/register.model.ts`. Editing a record that already
 * exists is `studio.model.ts`, whose PUT shape this request mirrors.
 *
 * The status dropdown is a produced count in disguise — Conceived is 0,
 * Produced is the whole edition, In production is a typed number between —
 * because nothing in the database stores the word. See `producedCountFor`.
 */

import type { RegisterStudioWorkRequest } from "@ondo/shared";

import { parseMoneyMinor } from "../lib/money.ts";
import { STUDIO_REGISTER_COPY } from "./estate.constants.ts";
import type { PendingImage } from "../register/register.model.ts";

/**
 * Registering a work in the studio: the draft, its refusals, and the request.
 *
 * The one translation worth stating is the status. The form shows Conceived /
 * In production / Produced because the design does, and the database refuses
 * to store any of those words (20260911091000 decision 3) — so what leaves
 * here is a PRODUCED COUNT, and the Studio card derives the same word back
 * from it. Conceived is 0; Produced is the whole edition; In production is a
 * number the artist types, between the two.
 */

export type StudioStatus = "conceived" | "in_production" | "produced";

export const STUDIO_STATUSES: readonly StudioStatus[] = [
  "conceived",
  "in_production",
  "produced",
];

/** The form as typed, nothing parsed yet. */
export interface StudioRegisterDraft {
  title: string;
  year: string;
  medium: string;
  dimensions: string;
  editionSize: string;
  status: StudioStatus;
  /** Only read when `status` is in_production. */
  producedSoFar: string;
  location: string;
  cost: string;
  insured: string;
  retail: string;
  series: string;
  currency: string;
  notes: string;
}

export const EMPTY_STUDIO_DRAFT: StudioRegisterDraft = {
  title: "",
  year: "",
  medium: "",
  dimensions: "",
  editionSize: "1",
  status: "conceived",
  producedSoFar: "",
  location: "",
  cost: "",
  insured: "",
  retail: "",
  series: "",
  currency: "GBP",
  notes: "",
};

/**
 * Which panel the dialog is showing.
 *
 * A stage union rather than a route, as the collector's wizard and the signup
 * flow do it: a step is not a place you can arrive at cold. `registered`
 * carries the result so the code cannot be shown before the insert minted it.
 */
export type StudioRegisterStage =
  | { kind: "details" }
  | { kind: "images" }
  | { kind: "provenance" }
  | { kind: "confirm" }
  | { kind: "registered"; workId: string; title: string; ondoCode: string };

export const STUDIO_REGISTER_STEPS = ["details", "images", "provenance", "confirm"] as const;

export function stepIndex(stage: StudioRegisterStage): number {
  if (stage.kind === "registered") return STUDIO_REGISTER_STEPS.length;

  return STUDIO_REGISTER_STEPS.indexOf(stage.kind);
}

/** A positive whole number, or null. */
function wholeNumber(typed: string): number | null {
  const trimmed = typed.trim();
  if (!/^\d+$/.test(trimmed)) return null;

  return Number(trimmed);
}

/** The edition size the draft names; null for a blank, NaN-free. */
export function editionSizeOf(draft: StudioRegisterDraft): number | null {
  const size = wholeNumber(draft.editionSize);

  return size === null || size === 0 ? null : size;
}

/**
 * The status, as a count. The one place the dropdown becomes a number.
 *
 * `null` for an In production draft with no count typed — the refusal below
 * names it, and this stays a pure translation rather than a second validator.
 */
export function producedCountFor(draft: StudioRegisterDraft): number | null {
  switch (draft.status) {
    case "conceived":
      return 0;
    case "produced":
      return editionSizeOf(draft) ?? 1;
    case "in_production":
      return wholeNumber(draft.producedSoFar);
  }
}

/**
 * Why the Details step cannot be left, or null. Client-side mirrors of the
 * database's own rules, in the order the form reads — the constraint still
 * decides.
 */
export function detailsRefusal(draft: StudioRegisterDraft): string | null {
  if (draft.title.trim() === "") return STUDIO_REGISTER_COPY.needsTitle;

  if (draft.editionSize.trim() !== "" && editionSizeOf(draft) === null) {
    return STUDIO_REGISTER_COPY.badEdition;
  }

  if (draft.status === "in_production") {
    const size = editionSizeOf(draft);
    const soFar = wholeNumber(draft.producedSoFar);
    if (soFar === null || soFar < 1) return STUDIO_REGISTER_COPY.needsProducedSoFar;
    if (size !== null && soFar >= size) return STUDIO_REGISTER_COPY.producedExceedsEdition(size);
  }

  const figures = [draft.cost, draft.insured, draft.retail, draft.series];
  for (const typed of figures) {
    if (typed.trim() !== "" && parseMoneyMinor(typed) === null) {
      return STUDIO_REGISTER_COPY.badFigure;
    }
  }

  const anyFigure = figures.some((typed) => typed.trim() !== "");
  if (anyFigure && !/^[A-Za-z]{3}$/.test(draft.currency.trim())) {
    return STUDIO_REGISTER_COPY.needsCurrency;
  }

  return null;
}

/** Blank to null, trimmed otherwise. */
function text(typed: string): string | null {
  const trimmed = typed.trim();

  return trimmed === "" ? null : trimmed;
}

/** The whole request. Every field sent, blanks as nulls — the record's own PUT shape. */
export function toStudioRequest(
  draft: StudioRegisterDraft,
  studioOndoId: string,
): RegisterStudioWorkRequest {
  const figure = (typed: string) => (typed.trim() === "" ? null : parseMoneyMinor(typed));
  const anyFigure = [draft.cost, draft.insured, draft.retail, draft.series].some(
    (typed) => typed.trim() !== "",
  );

  return {
    studioOndoId,
    title: draft.title.trim(),
    year: text(draft.year),
    medium: text(draft.medium),
    dimensions: text(draft.dimensions),
    editionSize: editionSizeOf(draft),
    producedCount: producedCountFor(draft),
    location: text(draft.location),
    costMinor: figure(draft.cost),
    insuredMinor: figure(draft.insured),
    retailMinor: figure(draft.retail),
    seriesMinor: figure(draft.series),
    currency: anyFigure ? draft.currency.trim().toUpperCase() : null,
    notes: text(draft.notes),
  };
}

/** One line of the Confirm step. */
export interface ConfirmRow {
  label: string;
  value: string;
}

/** What the Confirm step reads back, in the order the form asked. */
export function confirmRows(draft: StudioRegisterDraft, images: PendingImage[]): ConfirmRow[] {
  const size = editionSizeOf(draft);
  const produced = producedCountFor(draft);
  const dash = STUDIO_REGISTER_COPY.none;

  return [
    { label: STUDIO_REGISTER_COPY.year, value: text(draft.year) ?? dash },
    { label: STUDIO_REGISTER_COPY.medium, value: text(draft.medium) ?? dash },
    { label: STUDIO_REGISTER_COPY.dimensions, value: text(draft.dimensions) ?? dash },
    {
      label: STUDIO_REGISTER_COPY.edition,
      value:
        size === null
          ? dash
          : STUDIO_REGISTER_COPY.editionSummary(produced ?? 0, size),
    },
    { label: STUDIO_REGISTER_COPY.location, value: text(draft.location) ?? dash },
    {
      label: STUDIO_REGISTER_COPY.images,
      value: STUDIO_REGISTER_COPY.imageCount(images.length),
    },
  ];
}
